import { forwardRef, CSSProperties, ReactNode } from 'react';

import clsx from 'clsx';
import {
  Scrollbar as ReactScrollbarsCustom,
  ScrollbarProps as ReactScrollbarsCustomProps,
} from 'react-scrollbars-custom';
import { ScrollState } from 'react-scrollbars-custom/dist/types/types';

import * as styles from './Scrollbar.css';

export interface ScrollbarProps extends Omit<ReactScrollbarsCustomProps, 'ref' | 'onScroll'> {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /**
   * 스크롤바 타입
   * @default 'default'
   * - default: 일반 스크롤바 (Default: grayOpacity22, Hover: grayOpacity36)
   * - table: 테이블용 스크롤바 (Default: grayOpacity36, Hover: grayOpacity52)
   */
  type?: 'default' | 'table';
  /**
   * 가로 스크롤 표시 여부
   * @default false
   */
  noScrollX?: boolean;
  /**
   * 세로 스크롤 표시 여부
   * @default false
   */
  noScrollY?: boolean;
  /**
   * 스크롤바 wrapper에 적용할 스타일
   */
  wrapperStyle?: CSSProperties;
  /**
   * 스크롤바 컨테이너에 적용할 스타일
   */
  scrollbarStyle?: CSSProperties;
  trackXOffset?: {
    left?: number | string;
    right?: number | string;
    bottom?: number | string;
  };
  trackYOffset?: {
    top?: number | string;
    right?: number | string;
    bottom?: number | string;
  };
  onScroll?: (scrollValues: ScrollState, prevScrollState: ScrollState) => void;
}

export const Scrollbar = forwardRef<ReactScrollbarsCustom, ScrollbarProps>(
  (
    {
      children,
      className,
      style,
      type = 'default',
      noScrollX = false,
      noScrollY = false,
      wrapperStyle,
      scrollbarStyle,
      trackXOffset,
      trackYOffset,
      onScroll,
      ...props
    },
    ref,
  ) => {
    const scrollbarClass = type === 'table' ? styles.scrollbarTable : styles.scrollbarDefault;

    return (
      <ReactScrollbarsCustom
        ref={ref as any}
        className={clsx(scrollbarClass, className)}
        style={style}
        wrapperProps={{
          style: wrapperStyle,
        }}
        noScrollX={noScrollX}
        noScrollY={noScrollY}
        trackXProps={{
          className: styles.trackX,
          style: {
            left: trackXOffset?.left ?? 0,
            right: trackXOffset?.right ?? 0,
            bottom: trackXOffset?.bottom ?? 0,
            width: '100%',
          },
        }}
        trackYProps={{
          className: styles.trackY,
          style: {
            top: trackYOffset?.top ?? 0,
            right: trackYOffset?.right ?? 0,
            bottom: trackYOffset?.bottom ?? 0,
            height: '100%',
          },
        }}
        thumbXProps={{
          className: clsx(styles.thumbX, styles.thumbColor),
        }}
        thumbYProps={{
          className: clsx(styles.thumbY, styles.thumbColor),
        }}
        scrollerProps={{
          style: {
            paddingRight: 10,
            ...scrollbarStyle,
          },
        }}
        onScroll={onScroll as Required<ReactScrollbarsCustomProps>['onScroll']}
        {...props}
      >
        {children}
      </ReactScrollbarsCustom>
    );
  },
);

Scrollbar.displayName = 'Scrollbar';
